/*
 * recommend-lite.js
 * --------------------------------------------
 * ランキングベース推奨システム：localStorageの閲覧履歴 + 売れ筋ランキング
 * デザイン：browsing-history-products と統一されたスタイル
 *
 * 使い方：
 *  1) このファイルをテーマの assets に追加
 *  2) theme.liquid などで読み込み：
 *     <script src="{{ 'recommend-lite.js' | asset_url }}" defer></script>
 *  3) 各ページで：
 *     window.Reco.recommend('#rec-history', { limit: 6, title: '最近の閲覧に基づくおすすめ' });
 *
 *  前提：
 *   - 閲覧履歴は localStorage.ankerBrowsingHistory に [{handle, ts}] 形式で保存済み
 *   - 売れ筋ランキングJSONが /collections/{collection}?view=index.json で取得可能
 *     （collection.index.json.liquid テンプレートを設置）
 *
 *  ランキングベース推奨（v3）：
 *   - 売れ筋ランキングを主要な候補として使用
 *   - 閲覧履歴なし：各カテゴリー上位3位まで取得
 *   - 閲覧履歴あり：各カテゴリー上位15位まで取得
 *   - 商品キャッシュ不要、価格更新不要、常に最新情報
 *
 *  デザイン特徴：
 *   - browsing-history-products と同じレイアウト（3列→4列グリッド）
 *   - レスポンシブ対応（モバイル・デスクトップ）
 *   - 統一されたカードデザイン（画像1:1、角丸8px）
 *
 *  表示保証機能：
 *   - 最低6つ〜最大6つの商品表示（CONFIG.minDisplayCount = 6, maxDisplayCount = 6）
 *   - 段階的フォールバック：ランキング商品→関連商品→全商品
 *   - 履歴が少ない新規ユーザーにも適切な推薦を提供
 *
 *  カテゴリー多様性機能：
 *   - 同一カテゴリーの重複を避けて多様な商品を表示（CONFIG.diverseCategories = true）
 *   - ベンダー多様性と組み合わせて最適なバランスを実現
 *   - 集中閲覧時は同一カテゴリーの表示数を増加（CONFIG.maxPerCategoryFocused = 6）
 *
 *  スコアリング優先度：
 *   - カテゴリー一致（+2.0点）：最高優先度（ユーザーの興味を正確に反映）
 *   - ブランド一致（+1.5点）：中優先度（4ブランド体制での適切な重み付け）
 *   - 価格帯一致（+1.0点）：高スコア（購買意欲に直結）
 *   - ランキング順位（+0.5点）：売れ筋商品の優先表示
 */
(() => {
  // ⚙️ 設定
  const CONFIG = {
    // 閲覧履歴設定
    historyKey: "ankerBrowsingHistory", // 閲覧履歴のローカルストレージキー
    historyMax: 50, // 閲覧履歴の最大件数
    halfLifeHours: 24, // 閲覧履歴の半減期（時間）

    // 多様性制限
    maxPerVendor: 3, // ベンダーあたり最大商品数
    maxPerCategory: 3, // カテゴリーあたり最大商品数（基本値）
    maxPerCategoryFocused: 6, // 集中閲覧時のカテゴリーあたり最大商品数
    diverseCategories: true, // カテゴリー多様性を有効

    // 表示設定
    minDisplayCount: 6, // 最低表示数
    maxDisplayCount: 6, // 最大表示数
    candidateMultiplier: 8, // 候補数の倍率
    randomVariation: false, // ランダム化を無効

    // ランキング取得設定
    rankingLimitNoHistory: 3, // 閲覧履歴なし：各カテゴリーの上位何位まで取得
    rankingLimitWithHistory: 15, // 閲覧履歴あり：各カテゴリーの上位何位まで取得
  };

  const now = () => Date.now();

  // 📊 閲覧履歴の集中度を判定
  const getBrowsingFocus = (history) => {
    if (history.length === 0)
      return { isFocused: false, dominantCategory: null, focusRatio: 0 };

    // カテゴリー別の閲覧回数をカウント
    const categoryCounts = {};
    history.forEach((item) => {
      if (item.productType) {
        categoryCounts[item.productType] =
          (categoryCounts[item.productType] || 0) + 1;
      }
    });

    // 最も多く閲覧されたカテゴリーを特定
    const entries = Object.entries(categoryCounts);
    if (entries.length === 0)
      return { isFocused: false, dominantCategory: null, focusRatio: 0 };

    const [dominantCategory, count] = entries.reduce((max, current) =>
      current[1] > max[1] ? current : max
    );

    // 集中度を計算（最も多いカテゴリーの割合）
    const focusRatio = count / history.length;

    // 80%以上が同じカテゴリーの場合、集中閲覧と判定
    const isFocused = focusRatio >= 0.8;

    return { isFocused, dominantCategory, focusRatio };
  };

  const loadHistory = () => {
    try {
      const arr = JSON.parse(localStorage.getItem(CONFIG.historyKey) || "[]");
      if (!Array.isArray(arr)) return [];

      // 新しいankerBrowsingHistoryの構造に対応
      // {handle, ts} 形式のみ
      const result = arr
        .filter((item) => item && item.handle && item.ts) // handleとtsがあるもののみ
        .map((item) => ({
          handle: item.handle,
          ts: item.ts,
        }))
        .slice(0, CONFIG.historyMax);

      return result;
    } catch (e) {
      return [];
    }
  };

  const fetchJSON = async (url) => {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Fetch failed: ${url}`);
    return res.json();
  };

  const recencyWeight = (ts) => {
    const hours = (now() - ts) / 36e5;
    return Math.exp(-hours / CONFIG.halfLifeHours); // 0..1
  };

  const fetchProduct = async (handle) => {
    try {
      const p = await fetchJSON(`/products/${handle}.js`);
      return p;
    } catch (e) {
      return null;
    }
  };

  // 📈 シグナル構築：handleから商品情報を並列取得
  const buildSignals = async () => {
    const history = loadHistory();

    const vendorScore = new Map();
    const typeScore = new Map();
    const priceRef = [];

    // handleから商品情報を並列取得（高速化）
    const productPromises = history.map(async (item) => {
      const w = recencyWeight(item.ts || now());

      try {
        const product = await fetchProduct(item.handle);

        return { product, w, success: true };
      } catch (error) {
        return { product: null, w, success: false };
      }
    });

    const productResults = await Promise.allSettled(productPromises);

    // 結果を処理
    for (const result of productResults) {
      if (
        result.status === "fulfilled" &&
        result.value.success &&
        result.value.product
      ) {
        const { product, w } = result.value;

        // ベンダースコア
        if (product.vendor && product.vendor.trim()) {
          vendorScore.set(
            product.vendor,
            (vendorScore.get(product.vendor) || 0) + w
          );
        }

        // productTypeスコア
        if (product.type && product.type.trim()) {
          typeScore.set(product.type, (typeScore.get(product.type) || 0) + w);
        }

        // 価格情報
        if (product.price && typeof product.price === "number") {
          priceRef.push({ price: product.price / 100, w }); // 円単位に変換
        }
      }
    }

    // 平均価格計算
    const avgPrice = priceRef.length
      ? priceRef.reduce((s, { price, w }) => s + price * w, 0) /
        priceRef.reduce((s, { w }) => s + w, 0)
      : null;

    const topVendors = [...vendorScore.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k)
      .slice(0, 3);

    const topTypes = [...typeScore.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k)
      .slice(0, 3);

    return { topVendors, topTypes, avgPrice };
  };

  // 🚀 ランキング上位商品を取得（推奨候補の主要ソース）
  const getRankingRecommendations = async (limit, isHistoryMode = false) => {
    try {
      // 各カテゴリーのランキング上位商品を取得
      const rankingCollections = [
        "mobilebattery", // モバイルバッテリー
        "charger", // 充電器
        "cable", // ケーブル
        "usbhub", // USBハブ/アダプタ
        "powerhouse", // ポータブル電源
        "earphone-all", // イヤホン
        "headphone", // ヘッドホン
        "speaker", // スピーカー
        "cleaner-all", // 掃除機
        "projector-all", // プロジェクター
      ];

      const rankingPromises = rankingCollections.map(async (collection) => {
        try {
          const response = await fetch(
            `/collections/${collection}?view=index.json`
          );
          if (!response.ok) return null;

          const products = await response.json();

          if (isHistoryMode) {
            // 閲覧履歴ありの場合：各カテゴリーの上位商品から取得
            return (
              products
                .filter((p) => p.available)
                .slice(0, CONFIG.rankingLimitWithHistory) || []
            );
          } else {
            // 閲覧履歴なしの場合：各カテゴリーの上位商品から取得
            return (
              products
                .filter((p) => p.available)
                .slice(0, CONFIG.rankingLimitNoHistory) || []
            );
          }
        } catch (error) {
          return null;
        }
      });

      const rankingProducts = await Promise.all(rankingPromises);

      // 配列を平坦化して、指定された件数まで取得
      const validProducts = rankingProducts
        .filter((products) => products !== null)
        .flat()
        .slice(0, limit);

      return validProducts;
    } catch (error) {
      return [];
    }
  };

  // 📊 商品スコアリング（ランキングベース + 閲覧履歴）
  const scoreProduct = (p, pref) => {
    if (!p.available) return -Infinity;
    let s = 0;
    const scoreBreakdown = {
      category: 0,
      vendor: 0,
      price: 0,
      base: 0,
      random: 0,
    };

    // カテゴリー一致スコア（最高優先度）
    if (pref.topTypes.length > 0 && pref.topTypes.includes(p.product_type)) {
      s += 2.0;
      scoreBreakdown.category = 2.0;
    }

    // ブランド一致スコア（中優先度）
    if (pref.topVendors.length > 0 && pref.topVendors.includes(p.vendor)) {
      s += 1.5;
      scoreBreakdown.vendor = 1.5;
    }

    // 価格帯スコア（新機能）
    if (pref.avgPrice && p.price) {
      const userPrice = pref.avgPrice * 100; // 円単位に変換
      const productPrice = p.price; // センター単位
      const priceRatio = Math.abs(productPrice - userPrice) / userPrice;

      // 価格差が小さいほど高スコア（±30%以内で最大スコア）
      if (priceRatio <= 0.3) {
        s += 1.0; // 価格帯一致で高スコア
        scoreBreakdown.price = 1.0;
      } else if (priceRatio <= 0.5) {
        s += 0.5; // 価格帯近接で中スコア
        scoreBreakdown.price = 0.5;
      } else if (priceRatio <= 1.0) {
        s += 0.2; // 価格帯類似で低スコア
        scoreBreakdown.price = 0.2;
      }
      // 価格差が100%以上はスコア加算なし
    }

    // 履歴がない場合の基本スコア（新規ユーザー対応）
    if (pref.topVendors.length === 0 && pref.topTypes.length === 0) {
      s += 0.5; // 基本スコア
      scoreBreakdown.base = 0.5;
    }

    // ランダム要素は設定に基づいて追加
    if (CONFIG.randomVariation) {
      const randomFactor = (Math.random() - 0.5) * 0.2;
      s += randomFactor;
      scoreBreakdown.random = randomFactor;
    }

    return s;
  };

  const diversify = (list, maxPerVendor) => {
    const out = [];
    const perVendor = new Map();
    for (const p of list) {
      const c = perVendor.get(p.vendor) || 0;
      if (c >= maxPerVendor) continue;
      perVendor.set(p.vendor, c + 1);
      out.push(p);
    }
    return out;
  };

  // 🎯 カテゴリー多様性：同じproduct_typeの重複を避ける
  const diversifyByCategory = (list, maxPerCategory) => {
    const out = [];
    const perCategory = new Map();

    for (const p of list) {
      const category = p.product_type || "unknown";
      const c = perCategory.get(category) || 0;

      if (c >= maxPerCategory) {
        continue;
      }

      perCategory.set(category, c + 1);
      out.push(p);
    }

    return out;
  };

  // 🌈 総合多様性：ベンダーとカテゴリー両方を考慮
  const diversifyBoth = (list, maxPerVendor, maxPerCategory) => {
    if (!CONFIG.diverseCategories) {
      return diversify(list, maxPerVendor);
    }

    // より多くの候補を確保するため、まずベンダー多様性を適用、次にカテゴリー多様性を適用
    const vendorDiversified = diversify(list, maxPerVendor);
    return diversifyByCategory(vendorDiversified, maxPerCategory);
  };

  const uniqueByHandle = (arr) => {
    const seen = new Set();
    return arr.filter((p) => !seen.has(p.handle) && seen.add(p.handle));
  };

  // ランダムシャッフル関数
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 🎲 ランダム選択で多様性を確保
  const selectRandomVariety = (candidates, targetCount) => {
    if (candidates.length <= targetCount) {
      return shuffleArray(candidates);
    }

    // 上位商品とランダム商品を組み合わせ
    const topCount = Math.max(1, Math.floor(targetCount * 0.6)); // 60%を上位商品
    const randomCount = targetCount - topCount; // 残りをランダム商品

    // 上位商品を選択
    const topProducts = candidates.slice(0, topCount);

    // 残りの商品からランダム選択
    const remainingProducts = candidates.slice(topCount);
    const randomProducts = shuffleArray(remainingProducts).slice(
      0,
      randomCount
    );

    // 組み合わせてシャッフル
    const combined = [...topProducts, ...randomProducts];
    return shuffleArray(combined);
  };

  // ローディング状態を制御
  const showLoading = (target, title = "") => {
    const root =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!root) return;

    // 現在のターゲットを保存（リサイズ時の高さ再計算用）
    currentLoadingTarget = typeof target === "string" ? target : null;

    // レスポンシブに応じた高さを計算（実際のレンダリング高さに基づく）
    const isMobile = window.innerWidth < 1080;
    let calculatedHeight;

    if (isMobile) {
      calculatedHeight = 260;
    } else {
      calculatedHeight = 160;
    }

    // タイトルとローディングスピナーを表示（既存のコンテンツは保持）
    root.innerHTML = `
      <div class="reco-container">
        ${title ? `<div class="reco-head"><h3>${title}</h3></div>` : ""}
        <div class="reco-loading" style="min-height: ${calculatedHeight}px;">
          <div class="reco-loading-container">
            <div class="reco-loading-spinner"></div>
          </div>
        </div>
      </div>
    `;
  };

  const hideLoading = (target) => {
    const root =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!root) return;

    // ローディング要素のみを削除（タイトルは保持）
    const loadingElement = root.querySelector(".reco-loading");
    if (loadingElement) {
      loadingElement.remove();
    }

    // 現在のターゲットをクリア
    currentLoadingTarget = null;
  };

  // 商品グリッドのみを描画（タイトルは既に表示済み）
  const renderProducts = (target, items) => {
    const root =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!root || !items || !items.length) return;

    // 既存のコンテナ構造を保持し、商品グリッドのみ更新
    const existingContainer = root.querySelector(".reco-container");
    if (existingContainer) {
      // 既存のコンテナ内の商品グリッドを探す
      let existingGrid = existingContainer.querySelector(".reco-grid");

      // 商品グリッドが存在しない場合は作成
      if (!existingGrid) {
        existingGrid = document.createElement("div");
        existingGrid.className = "reco-grid";
        existingGrid.setAttribute("data-rec-source", "history");
        existingContainer.appendChild(existingGrid);
      }

      // 商品グリッドの内容を更新
      existingGrid.innerHTML = items
        .map((p) => {
          // URLにvariantパラメーターとrefパラメーターを追加
          let url = p.url;

          // variantパラメーターを追加
          if (p.first_variant_id) {
            url += url.includes("?") ? "&" : "?";
            url += `variant=${p.first_variant_id}`;
          }

          // refパラメーターを追加
          url += url.includes("?") ? "&" : "?";
          url += "ref=reco_history";

          return `
        <a href="${url}" class="reco-card reco-data-link" data-handle="${
            p.handle
          }">
          <div class="reco-image-container">
            <img loading="lazy" src="${p.image}" alt="${p.title}">
          </div>
          <div class="reco-content">
            ${
              p.compare_at_price && p.compare_at_price > p.price
                ? `
                                        <div class="reco-price-container">
                        <div class="reco-discount-row">
                          <span class="reco-discount">${Math.round(
                            ((p.compare_at_price - p.price) /
                              p.compare_at_price) *
                              100
                          )}%OFF</span>
                        </div>
                        <div class="reco-price-row">
                          <span class="reco-price-original">¥${Number(
                            p.compare_at_price / 100
                          ).toLocaleString()}</span>
                          <span class="reco-price-sale">¥${Number(
                            p.price / 100
                          ).toLocaleString()}</span>
                        </div>
                      </div>
                `
                : p.price
                ? `<span class="reco-price">¥${Number(
                    p.price / 100
                  ).toLocaleString()}</span>`
                : ""
            }
          </div>
        </a>
      `;
        })
        .join("");
    }
  };

  // 完全なHTMLを描画（初期表示用）
  const render = (target, items, title) => {
    const root =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!root || !items || !items.length) return;

    const html = `
      <div class="reco-container">
        ${title ? `<div class="reco-head"><h3>${title}</h3></div>` : ""}
        <div class="reco-grid" data-rec-source="history">
          ${items
            .map((p) => {
              // URLにvariantパラメーターとrefパラメーターを追加
              let url = p.url;

              // variantパラメーターを追加
              if (p.first_variant_id) {
                url += url.includes("?") ? "&" : "?";
                url += `variant=${p.first_variant_id}`;
              }

              // refパラメーターを追加
              url += url.includes("?") ? "&" : "?";
              url += "ref=reco_history";

              return `
            <a href="${url}" class="reco-card" data-handle="${p.handle}">
              <div class="reco-image-container">
                <img loading="lazy" src="${p.image}" alt="${p.title}">
              </div>
              <div class="reco-content">
                ${
                  p.compare_at_price && p.compare_at_price > p.price
                    ? `
                      <div class="reco-price-container">
                        <div class="reco-discount-row">
                          <span class="reco-discount">${Math.round(
                            ((p.compare_at_price - p.price) /
                              p.compare_at_price) *
                              100
                          )}%OFF</span>
                        </div>
                        <div class="reco-price-row">
                          <span class="reco-price-original">¥${Number(
                            p.compare_at_price / 100
                          ).toLocaleString()}</span>
                          <span class="reco-price-sale">¥${Number(
                            p.price / 100
                          ).toLocaleString()}</span>
                        </div>
                      </div>
                    `
                    : p.price
                    ? `<span class="reco-price">¥${Number(
                        p.price / 100
                      ).toLocaleString()}</span>`
                    : ""
                }
              </div>
            </a>
          `;
            })
            .join("")}
        </div>
      </div>`;
    root.innerHTML = html;
  };

  const executeRecommend = async (target, opts = {}) => {
    const { limit = 12, title = "" } = opts;

    // ローディング状態を表示（タイトル付き）
    showLoading(target, title);

    try {
      // 閲覧履歴を先に取得
      const browsingHistory = loadHistory();
      const hasHistory = browsingHistory.length > 0;

      // 閲覧履歴の集中度を判定
      const browsingFocus = getBrowsingFocus(browsingHistory);

      // 動的にカテゴリー制限を調整
      const effectiveMaxPerCategory = browsingFocus.isFocused
        ? CONFIG.maxPerCategoryFocused
        : CONFIG.maxPerCategory;

      // 閲覧履歴がない場合は、ランキング上位商品を表示して終了
      if (!hasHistory) {
        // ランキング上位商品を表示（6件固定）
        const rankingRecommendations = await getRankingRecommendations(
          6,
          false
        );
        if (rankingRecommendations.length > 0) {
          render(target, rankingRecommendations, "あなたへのおすすめ");
          hideLoading(target);
          return; // 履歴がない場合はここで終了
        }
      }

      // 閲覧履歴がある場合：シグナル構築とランキング取得を並列実行（表示速度を改善）
      const [pref, rankingProducts] = await Promise.all([
        buildSignals(),
        getRankingRecommendations(150, true).catch(() => []),
      ]);

      // 🎯 カテゴリー名の正規化
      const availableCategories = [
        ...new Set(
          rankingProducts
            .filter((p) => p.available && p.product_type)
            .map((p) => p.product_type)
        ),
      ];

      // 簡単なカテゴリー名の正規化
      const categoryMapping = {
        Speaker: "Wireless",
        Speakers: "Wireless",
        Audio: "Wireless",
      };

      const normalizedTopTypes = pref.topTypes
        .map((category) => {
          // マッピングテーブルで検索
          if (categoryMapping[category]) {
            const mappedCategory = categoryMapping[category];
            if (availableCategories.includes(mappedCategory)) {
              return mappedCategory;
            }
          }
          // 直接一致をチェック
          return availableCategories.includes(category) ? category : null;
        })
        .filter((category) => category !== null);

      // ランキング商品から関連商品をフィルタリング
      let pool = rankingProducts.filter(
        (p) =>
          p.available &&
          (pref.topVendors.includes(p.vendor) ||
            (normalizedTopTypes.length > 0 &&
              normalizedTopTypes.includes(p.product_type)))
      );

      const poolCategoryStats = {};
      pool.forEach((p) => {
        const category = p.product_type || "unknown";
        poolCategoryStats[category] = (poolCategoryStats[category] || 0) + 1;
      });

      // より多くの候補を取得（限界の2-3倍）
      const expandedLimit = Math.min(
        limit * CONFIG.candidateMultiplier,
        pool.length
      );

      // 正規化されたカテゴリー名を使用してスコアリング
      const ranked = pool
        .map((p) => ({
          ...p,
          __score: scoreProduct(p, {
            ...pref,
            topTypes: normalizedTopTypes,
          }),
        }))
        .filter((p) => p.__score > 0)
        .sort((a, b) => b.__score - a.__score);

      let picked = diversifyBoth(
        uniqueByHandle(ranked),
        CONFIG.maxPerVendor,
        effectiveMaxPerCategory
      ).slice(
        0,
        expandedLimit // より多くの候補を確保
      );

      // 🛡️ 段階的フォールバック戦略で最低表示数を保証
      const seen = new Set(picked.map((p) => p.handle));
      const usedCategories = new Set(picked.map((p) => p.product_type));

      // Step 1: 価格帯類似商品で補完（カテゴリー重複避ける）
      if (picked.length < CONFIG.minDisplayCount && pref.avgPrice) {
        const userPrice = pref.avgPrice; // セント単位のまま

        const priceSimilarProducts = rankingProducts
          .filter(
            (p) =>
              p.available &&
              p.price &&
              !seen.has(p.handle) &&
              (!CONFIG.diverseCategories ||
                !usedCategories.has(p.product_type)) &&
              Math.abs(p.price - userPrice) / userPrice <= 0.5 // ±50%以内
          )
          .sort((a, b) => {
            // 価格差が小さい順にソート
            const aDiff = Math.abs(a.price - userPrice) / userPrice;
            const bDiff = Math.abs(b.price - userPrice) / userPrice;
            return aDiff - bDiff;
          })
          .slice(0, CONFIG.minDisplayCount - picked.length);

        priceSimilarProducts.forEach((p) => {
          seen.add(p.handle);
          if (CONFIG.diverseCategories) usedCategories.add(p.product_type);
        });
        picked = picked.concat(priceSimilarProducts);
      }

      // Step 2: ランキング商品から補完（カテゴリー重複避ける）
      if (picked.length < CONFIG.minDisplayCount) {
        const fallbackCandidates = rankingProducts.filter(
          (p) =>
            p.available &&
            !seen.has(p.handle) &&
            (!CONFIG.diverseCategories || !usedCategories.has(p.product_type))
        );

        // ランダム化が無効の場合は、handleでソートして一貫性を保つ
        const fallbackProducts = CONFIG.randomVariation
          ? shuffleArray(fallbackCandidates).slice(
              0,
              CONFIG.minDisplayCount - picked.length
            )
          : fallbackCandidates
              .sort((a, b) => a.handle.localeCompare(b.handle))
              .slice(0, CONFIG.minDisplayCount - picked.length);

        fallbackProducts.forEach((p) => {
          seen.add(p.handle);
          if (CONFIG.diverseCategories) usedCategories.add(p.product_type);
        });
        picked = picked.concat(fallbackProducts);
      }

      // Step 3: カテゴリー制限を緩和した最終フォールバック
      if (picked.length < CONFIG.minDisplayCount && CONFIG.diverseCategories) {
        const finalCandidates = rankingProducts.filter(
          (p) => p.available && !seen.has(p.handle)
        );

        const finalFallback = CONFIG.randomVariation
          ? shuffleArray(finalCandidates).slice(
              0,
              CONFIG.minDisplayCount - picked.length
            )
          : finalCandidates
              .sort((a, b) => a.handle.localeCompare(b.handle))
              .slice(0, CONFIG.minDisplayCount - picked.length);

        picked = picked.concat(finalFallback);
      }

      // 🎲 最終選択（設定に基づく）
      const finalSelection = CONFIG.randomVariation
        ? selectRandomVariety(
            picked,
            Math.min(
              Math.max(limit, CONFIG.minDisplayCount),
              CONFIG.maxDisplayCount
            )
          )
        : picked.slice(
            0,
            Math.min(
              Math.max(limit, CONFIG.minDisplayCount),
              CONFIG.maxDisplayCount
            )
          );

      // 閲覧履歴がない場合はタイトルを変更
      const displayTitle =
        browsingHistory.length === 0 ? "あなたへのおすすめ" : title;

      // 最終選択された商品をそのまま使用（ランキング商品は最新情報）
      const finalSelectionWithUpdatedPrices = finalSelection;

      // タイトルは既に表示されているので、商品グリッドのみ更新
      renderProducts(target, finalSelectionWithUpdatedPrices);

      // ローディング状態を非表示
      hideLoading(target);
    } catch (error) {
      // エラー時はローディング状態を非表示してエラーメッセージを表示
      hideLoading(target);
      const root =
        typeof target === "string" ? document.querySelector(target) : target;
      if (root) {
        root.innerHTML = `
        <div class="reco-container">
          <div class="reco-head">
            <h3>おすすめ商品</h3>
          </div>
          <div class="reco-error">
            <p>おすすめ商品の読み込みに失敗しました。しばらく時間をおいて再度お試しください。</p>
          </div>
        </div>
      `;
      }
    }
  };

  const recommend = (target, opts = {}) => {
    const root =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!root) return;

    // 即時実行（IntersectionObserverによる遅延を廃止し、表示速度を改善）
    executeRecommend(target, opts);
  };

  // 新しいレコメンドデザイン（モバイル3列、PC6列）
  const STYLE = `
  
    /* ローディング状態 */
    .reco-loading {
      width: 100%;
      max-width: 1080px;
      margin: 0 auto;
      padding: 12px;
      background-color: transparent;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 160px; /* 実際の商品表示高さに基づく */
    }
    
    .reco-loading-container {
      text-align: center;
    }
    
    .reco-loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(0, 169, 224, 0.2);
      border-top-color: #00a9e0;
      border-radius: 50%;
      animation: reco-spin 0.8s linear infinite;
      margin: 0 auto 16px;
    }
    
    @keyframes reco-spin {
      to { transform: rotate(360deg); }
    }
    
    .reco-error {
      text-align: center;
      padding: 20px;
      color: #666;
    }
    
    .reco-error p {
      margin: 0;
      font-size: 14px;
    }
    
    .reco-container {
      width: 100%;
      max-width: 1080px;
      margin: 20px auto 50px;
      padding: 12px;
      background-color: #F2F4F5;
      border-radius: 4px;
    }
    
    .reco-head {
      margin-bottom: 10px;
    }
    
    .reco-head h3 {
      font-size: 14px;
      font-weight: normal;
      color: #333;
      margin: 0;
    }
    
    .reco-grid {
      display: grid;
      gap: 6px;
      grid-template-columns: repeat(3, 1fr);
    }
    
    .reco-card {
      display: flex;
      flex-direction: column;
      background: #fff;
      border-radius: 8px;
      padding: 5px;
      text-decoration: none;
      color: inherit;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      aspect-ratio: 1;
      overflow: hidden;
    }
    
    .reco-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
    
    .reco-image-container {
      width: 100%;
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
      overflow: hidden;
    }
    
    .reco-image-container img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
    
    .reco-content {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    
    .reco-price {
      font-size: 11px;
      color: #666666;
      line-height: 1.2;
    }
    
    .reco-price::after {
      content: " (税込)";
      font-size: 9px;
      font-weight: normal;
    }
    
    .reco-price-container {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    
    .reco-price-row {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-wrap: wrap;
    }
    
    .reco-price-original {
      font-size: 10px;
      color: #999;
      text-decoration: line-through;
      line-height: 1.2;
    }
    
    .reco-price-sale {
      font-size: 11px;
      color: #333;
      font-weight: bold;
      line-height: 1.2;
    }
    
    .reco-price-sale::after {
      content: " (税込)";
      font-size: 9px;
      font-weight: normal;
    }
    
    .reco-discount {
      font-size: 10px;
      color: #fff;
      background: #CD3752;
      padding: 4px;
      display: inline-block;
      white-space: nowrap;
      line-height: 1;
      border-radius: 2px;
      max-width: 50px;
    }
    
    @media (min-width: 1080px) {      
      .reco-container {
        padding: 20px;
      }
      .reco-head h3 {
        font-size: 18px;
      }
      .reco-grid {
        grid-template-columns: repeat(6, 1fr);
        gap: 12px;
      }
      
      .reco-image-container {
        margin-bottom: 12px;
      }
      
      .reco-content {
        gap: 6px;
      }
      
      .reco-price {
        font-size: 12px;
      }
      
      .reco-price::after {
        font-size: 10px;
      }
      
      .reco-price-original {
        font-size: 11px;
      }
      
      .reco-price-sale {
        font-size: 12px;
      }
      
      .reco-price-sale::after {
        font-size: 10px;
      }
    
    }
  `;
  try {
    const s = document.createElement("style");
    s.textContent = STYLE;
    document.head.appendChild(s);
  } catch (e) {}

  // 公開API
  window.Reco = {
    recommend,
    showLoading,
    hideLoading,
  };

  // 初期化完了を通知
  window.RecoInitialized = true;
})();
